import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type { PricingCampaignModel, ScenarioModel } from '@/shared/generated'
import type { PricingCampaignTableRow } from '@/widgets/scenario-pricing-campaigns'

import React, { Fragment, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	entries,
	find,
	fromPairs,
	indexBy,
	isEmpty,
	map,
	pipe,
	prop,
	propEq,
	propOr,
} from 'lodash/fp'
import { ChevronRightIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useOptimizationQuery } from '@/entities/optimization'
import {
	getPreOrderedCampaigns,
	isGlobalPricingCampaign,
	useDublicatePricingCampaignsMutation,
	useMatchedProductsQuery,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'
import { useScenariosQuery } from '@/entities/scenario'

import {
	getPricingCampaignsTableRows,
	ScenarioPricingCampaignsTable,
} from '@/widgets/scenario-pricing-campaigns'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTrigger,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'

type Props = {
	toScenarioId: string
	toScenarioName: string
	optimizationGroupId: string
}
function CopyPricingCampaignToScenarioModalContent({
	toScenarioId,
	toScenarioName,
	optimizationGroupId,
	close,
}: Props & { close: () => void }) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [rowSelection, setRowSelection] = useState<SRT_RowSelectionState>({})
	const scenarioQuery = useScenariosQuery<ScenarioModel | undefined>(
		optimizationGroupId!,
		{
			select: find<ScenarioModel>({ is_main: true }),
			enabled: !!optimizationGroupId,
		}
	)
	const scenario = scenarioQuery.data
	const campaigns = usePricingCampaignsQuery<PricingCampaignModel[]>(
		{
			optimization_group_id: optimizationGroupId,
		},
		{
			select: getPreOrderedCampaigns,
		}
	)
	const predictedCounts = useMatchedProductsQuery(
		{
			scenario_id: toScenarioId,
			pricing_campaigns:
				campaigns.data?.map((campaign) => ({
					id: campaign.id,
					order: campaign.order,
				})) || [],
		},
		{
			select: pipe([
				indexBy(prop('id')),
				entries,
				map(([id, { product_count }]) => [id, product_count]),
				fromPairs,
			]),
			enabled: !isEmpty(campaigns.data),
		}
	)
	const totalProducts = useOptimizationQuery<number>(optimizationGroupId, {
		select: propOr(0, 'total_products'),
	})
	const dublicate = useDublicatePricingCampaignsMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-segments'],
			})
			close()
		},
	})
	const selectedCampaigns = useMemo(
		() => Object.keys(rowSelection),
		[rowSelection]
	)
	const data: PricingCampaignTableRow[] = useMemo(
		() =>
			getPricingCampaignsTableRows(
				campaigns.data || [],
				predictedCounts.data || {},
				totalProducts.data
			),
		[campaigns.data, predictedCounts.data, totalProducts.data]
	)
	const handleSubmit = () => {
		ampli.whatIfDuplicatePcsToScenarioModalSubmit({
			pcs: selectedCampaigns.length,
		})
		dublicate.mutate({
			src_pricing_campaign_ids: selectedCampaigns,
			dst_entity_ids: [toScenarioId],
			dst_entity_type: 'scenario',
		})
	}
	const isValidSelection = useMemo(
		() =>
			selectedCampaigns.length > 0 &&
			selectedCampaigns.every((id: string) => {
				const pc = find(propEq('campaign.id', id), data)

				return pc?.campaign && !isGlobalPricingCampaign(pc.campaign)
			}),
		[selectedCampaigns, data]
	)

	return (
		<Fragment>
			<ModalHeader>
				<ModalTitle>
					{t('scenario.copy_pcs.modal.title', {
						scenarioName: toScenarioName,
						defaultValue:
							'Select Pricing Campaigns that you want to apply settings to',
					})}
				</ModalTitle>
				<ModalDescription>
					{t('scenario.copy_pcs.modal.subtitle', {
						defaultValue: `Select PCs to copy to ${toScenarioName}`,
					})}
				</ModalDescription>
			</ModalHeader>
			<ErrorBoundary>
				<ModalBody className='h-96 flex-1 flex-col'>
					{!data.length && !campaigns.isLoading && !scenarioQuery.isLoading ? (
						<div className='flex size-full flex-col items-center justify-center space-y-2 text-sm'>
							<SearchIcon height={72} width={72} />
							<p className='font-medium text-muted-foreground'>
								{t('scenario.empty_pcs.title')}
							</p>
						</div>
					) : (
						<ScenarioPricingCampaignsTable
							scenario={scenario}
							data={data}
							isLoading={campaigns.isLoading || scenarioQuery.isLoading}
							rowSelection={rowSelection}
							setRowSelection={setRowSelection}
						/>
					)}
				</ModalBody>
			</ErrorBoundary>
			<ModalFooter className='justify-between space-x-2'>
				<ModalClose asChild>
					<Button
						onClick={() => {
							ampli.whatIfDuplicatePcsToScenarioModalOpen()
						}}
					>
						{t('general_cancel')}
					</Button>
				</ModalClose>
				<Button
					variant='primary'
					onClick={handleSubmit}
					disabled={
						dublicate.isPending ||
						selectedCampaigns.length === 0 ||
						!isValidSelection
					}
				>
					{t('scenario.copy_to_scenario')}
					{dublicate.isPending ? <Spinner /> : <ChevronRightIcon />}
				</Button>
			</ModalFooter>
		</Fragment>
	)
}
export const DuplicateScenarioPricingCampaignModal = ({
	children,
	...props
}: Props & { children: React.ReactNode }) => {
	const { t } = useTranslation()

	const [isOpen, setIsOpen] = useState(false)

	return (
		<Modal open={isOpen} onOpenChange={setIsOpen}>
			<Tooltip>
				<TooltipTrigger asChild>
					<ModalTrigger asChild>{children}</ModalTrigger>
				</TooltipTrigger>
				<TooltipContent>{t('scenario.add_pcs')}</TooltipContent>
			</Tooltip>
			<ModalContent className='max-w-5xl'>
				<CopyPricingCampaignToScenarioModalContent
					{...props}
					close={() => setIsOpen(false)}
				/>
			</ModalContent>
		</Modal>
	)
}
